import React from 'react';
import { FileQuestion } from 'lucide-react';
import { SeverityMeter, SIGNAL_DISCLAIMER, severityFromScore } from './SeverityMeter';
import type { ReportMessage } from '../../../utils/scanReport';
interface EvidenceBreakdownProps {
  messages: ReportMessage[];
  /** True when the full conversation is on file (shown in the Conversation tab). */
  hasConversation: boolean;
  onViewConversation: () => void;
}

/** Full report: message-by-message evidence the analyzer flagged. */
export function EvidenceBreakdown({
  messages,
  hasConversation,
  onViewConversation
}: EvidenceBreakdownProps) {
  if (messages.length === 0) {
    return <div className="rounded-xl border border-dashed border-forensic-line p-8 text-center" data-id="element-189" data-mp="21tnivvl2yyxc">
        <FileQuestion className="mx-auto text-forensic-subtle" size={28} aria-hidden="true" data-id="element-190" data-mp="1wvm2jif1hdpk" />
        <h3 className="mt-3 text-base font-semibold text-white" data-id="element-191" data-mp="2bt4nsasid8s9">
          No message-level evidence on file
        </h3>
        <p className="mx-auto mt-1 max-w-md text-sm text-forensic-muted" data-id="element-192" data-mp="26r5md6gbwlxy">
          {hasConversation ? 'This scan didn’t store a per-message breakdown. The full conversation the analyzer received is available in the Conversation tab.' : 'The original conversation isn’t on file for this scan. Run a new scan to see message-by-message evidence.'}
        </p>
        {hasConversation && <button type="button" onClick={onViewConversation} className="mt-5 inline-flex min-h-[44px] items-center rounded-xl border border-forensic-line-strong px-5 text-sm font-semibold text-white transition-colors duration-150 ease-out hover:border-white/30" data-id="element-193" data-mp="3v2ieeldu4rad">
            View conversation
          </button>}
      </div>;
  }
  return <section className="overflow-hidden rounded-xl border border-forensic-line bg-forensic-surface" data-id="element-194" data-mp="hrt1ku78i6ae">
      <header className="border-b border-forensic-line px-5 py-5 sm:px-6" data-id="element-195" data-mp="mn8glpgza88e">
        <h3 className="text-lg font-semibold text-white" data-id="element-196" data-mp="rofl9fntqrau">Message-by-message evidence</h3>
        <p className="mt-1 text-sm text-forensic-muted" data-id="element-197" data-mp="2nru1ifk8t8oz">
          Each message the analyzer extracted, with its signal strength and flags.
        </p>
        <p className="mt-2 text-xs text-forensic-subtle" data-id="element-198" data-mp="2xgu7wk12r6d7">{SIGNAL_DISCLAIMER}</p>
      </header>
      <ol className="divide-y divide-forensic-line" data-id="element-199" data-mp="2sqnhcen05bsb">
        {messages.map((msg, i) => {
        const sev = typeof msg.severity === 'number' ? msg.severity : 0;
        const flags = Array.isArray(msg.flags) ? msg.flags : [];
        return <li key={i} className="px-5 py-5 sm:px-6" data-id="element-200" data-mp="32fnmkfs3l3ey" data-mpi={i}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4" data-id="element-201" data-mp="3gghsmrroasag">
                <span className="hidden w-6 shrink-0 pt-0.5 text-xs text-forensic-subtle tabular-nums sm:block" data-id="element-202" data-mp="3bh4xljqneews">
                  {i + 1}
                </span>
                <p className="flex-1 text-sm leading-relaxed text-gray-200" data-id="element-203" data-mp="1m2728evfjq2k">
                  {msg.text || '(empty message)'}
                </p>
                <SeverityMeter severity={severityFromScore(sev)} data-id="element-204" data-mp="3spo0mx9nc4c2" />
              </div>
              {(flags.length > 0 || msg.short_reason) && <div className="mt-3 flex flex-wrap items-center gap-1.5 sm:ml-10" data-id="element-205" data-mp="1j4ones2q8c6">
                  {flags.map(flag => <span key={flag} className="rounded-md bg-white/[0.05] px-2 py-0.5 text-[11px] text-forensic-muted" data-id="element-206" data-mp="3nzhd70qvepve">
                      {flag}
                    </span>)}
                  {msg.short_reason && <span className="text-xs text-forensic-subtle" data-id="element-207" data-mp="301ju69j93n8b">{msg.short_reason}</span>}
                </div>}
            </li>;
      })}
      </ol>
    </section>;
}